import { useQuery } from '@tanstack/react-query';
import { siteApi } from '@/features/site/api/siteApi';

/**
 * The current shop's website: name and landing-page content.
 *
 * Fresh for half a minute, so moving between pages of the shop costs no
 * requests - and re-checked whenever the tab regains focus, however fresh.
 * That second part is what makes the editor usable: the admin saves in one
 * tab (often on a different subdomain, so no cache is shared between them)
 * and switches to the shop's tab expecting to see the change. A five-minute
 * cache showed them the old page and looked exactly like a failed save.
 */
export function useSite() {
  return useQuery({
    queryKey: ['site'],
    queryFn: siteApi.site,
    staleTime: 30_000,
    refetchOnWindowFocus: 'always',
    // A closed or unknown shop is an answer, not a blip worth retrying.
    retry: (count, error) => ![403, 404].includes(error?.status) && count < 2,
  });
}

/** Category tiles and product rails. Re-checked on focus for the same reason:
 *  picking featured products is an edit too. */
export function useShowcase({ enabled = true } = {}) {
  return useQuery({
    queryKey: ['site', 'showcase'],
    queryFn: siteApi.showcase,
    staleTime: 30_000,
    refetchOnWindowFocus: 'always',
    enabled,
  });
}
